// ---------------------------------------------------------------------------
// ImageHotspot Renderer (editor key: ImageHotspot)
// ---------------------------------------------------------------------------
// Annotated image. Operators enter hotspots as pipe-delimited lines in the
// editor textarea: "X% | Y% | Label". We parse those into numbered markers
// stacked over the base image with a small caption next to each. Coordinates
// are clamped to 0..100 so a typo never escapes the frame. Static markup
// only — captions are always visible (no hover/JS) for accessibility and to
// avoid relying on runtime hydration.

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, imageUrl } from "../escape.js";

interface ImageHotspotProps {
  imageUrl?: string;
  hotspots?: string;
}

interface Hotspot {
  x: number;
  y: number;
  label: string;
}

function clampPercent(value: string): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 50;
  if (numeric < 0) return 0;
  if (numeric > 100) return 100;
  return numeric;
}

function parseHotspots(raw: unknown): Hotspot[] {
  if (typeof raw !== "string") return [];
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line) => {
      const [xRaw, yRaw, labelRaw] = line.split("|").map((part) => part.trim());
      return {
        x: clampPercent(xRaw ?? "50"),
        y: clampPercent(yRaw ?? "50"),
        label: labelRaw ?? "",
      };
    });
}

function renderHotspot(spot: Hotspot, index: number): string {
  const number = index + 1;
  const labelHtml = spot.label
    ? `\n        <span class="ease-image-hotspot__label">${text(spot.label)}</span>`
    : "";
  return `      <li class="ease-image-hotspot__item" style="position:absolute;left:${attr(String(spot.x))}%;top:${attr(String(spot.y))}%;transform:translate(-50%,-50%)">
        <span class="ease-image-hotspot__marker" aria-label="Hotspot ${attr(String(number))}">${number}</span>${labelHtml}
      </li>`;
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as ImageHotspotProps;

  const src = typeof p.imageUrl === "string" ? p.imageUrl.trim() : "";
  const hotspots = parseHotspots(p.hotspots);

  if (!src) {
    return "<!-- image-hotspot missing imageUrl -->";
  }

  const items = hotspots.map(renderHotspot).join("\n");
  const itemsHtml = items
    ? `\n    <ol class="ease-image-hotspot__list" role="list" style="position:absolute;inset:0;margin:0;padding:0;list-style:none">
${items}
    </ol>`
    : "";

  return `<section class="ease-section ease-image-hotspot">
  <div class="ease-image-hotspot__frame" style="position:relative;aspect-ratio:16/9;border-radius:12px;overflow:hidden">
    <img src="${imageUrl(src)}" alt="" loading="lazy" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover" />${itemsHtml}
  </div>
</section>`;
}

registerRenderer("image-hotspot", { render });
